import React from "react";
import { Box, Container, Typography, Grid, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import logoImg from "../../assets/images/talktokam.webp";
import { BRAND } from "../../theme/talktokam";

const Footer = () => {
  const navigate = useNavigate();

  const socialLinks = [
    { label: "Twitter", d: "M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" },
    { label: "LinkedIn", d: "M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2zM4 6a2 2 0 100-4 2 2 0 000 4z" },
    { label: "GitHub", d: "M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 00-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0020 4.77 5.07 5.07 0 0019.91 1S18.73.65 16 2.48a13.38 13.38 0 00-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 005 4.77a5.44 5.44 0 00-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 009 18.13V22" },
    { label: "YouTube", d: "M22.54 6.42a2.78 2.78 0 00-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 00-1.94 2A29 29 0 001 11.75a29 29 0 00.46 5.33A2.78 2.78 0 003.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 001.94-2 29 29 0 00.46-5.25 29 29 0 00-.46-5.33zM9.75 15.02V8.48l5.75 3.27-5.75 3.27z" },
  ];

  const footerColumns = [
    { title: "Product", links: [{ l: "Website Bot", to: "/website-bot" }, { l: "Features", to: "/features" }, { l: "Pricing", to: "/pricing" }, { l: "Integrations", to: "/integrations" }, { l: "API Docs", to: "/api-docs" }, { l: "Changelog", to: "/changelog" }] },
    { title: "Company", links: [{ l: "About", to: "/about" }, { l: "Blog", to: "/blog" }, { l: "Careers", to: "/careers" }, { l: "Press Kit", to: "/press-kit" }, { l: "Contact", to: "/contact" }] },
    { title: "Resources", links: [{ l: "Help Center", to: "/help-center" }, { l: "Documentation", to: "/docs" }, { l: "Community", to: "/community" }, { l: "Webinars", to: "/webinars" }] },
    { title: "Legal", links: [{ l: "Privacy Policy", to: "/privacy-policy" }, { l: "Terms of Service", to: "/terms-of-service" }, { l: "Cookie Policy", to: "/cookie-policy" }] },
  ];

  return (
    <Box sx={{ position: "relative", overflow: "hidden", background: "linear-gradient(180deg, #050816 0%, #02030a 100%)", pt: { xs: 8, md: 12 } }}>
      <Box sx={{ position: "absolute", top: "20%", left: "10%", width: 400, height: 400, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.cyan}14 0%, transparent 70%)`, filter: "blur(80px)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", top: "30%", right: "15%", width: 400, height: 400, borderRadius: "50%", background: `radial-gradient(circle, ${BRAND.purple}14 0%, transparent 70%)`, filter: "blur(80px)", pointerEvents: "none" }} />
      <Box sx={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(rgba(56,189,248,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.03) 1px, transparent 1px)`, backgroundSize: "60px 60px", pointerEvents: "none" }} />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <Box sx={{ textAlign: "center", py: { xs: 4, md: 6 }, mb: { xs: 6, md: 8 } }}>
            <Typography sx={{ fontSize: { xs: 12, md: 14 }, fontWeight: 700, color: BRAND.cyan, textTransform: "uppercase", letterSpacing: "3px", mb: 2 }}>
              Talktokam
            </Typography>
            <Typography sx={{ fontSize: { xs: 40, md: 72, lg: 88 }, fontWeight: 900, color: "#fff", letterSpacing: "-4px", lineHeight: 0.95, mb: 4, background: "linear-gradient(180deg, #ffffff 0%, #ffffff 40%, rgba(255,255,255,0.1) 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Let's build<br />something great.
            </Typography>
            <Box sx={{ display: "inline-flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={() => navigate("/signup")}
                endIcon={<ArrowForwardIcon />}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 700, px: 4, py: 1.6, fontSize: 15,
                  background: `linear-gradient(135deg, ${BRAND.cyan}, ${BRAND.purple})`,
                  boxShadow: `0 10px 30px ${BRAND.cyan}55`,
                  "&:hover": { boxShadow: `0 14px 40px ${BRAND.purple}77`, background: `linear-gradient(135deg, ${BRAND.cyanDark}, ${BRAND.purpleDark})`, transform: "translateY(-2px)" },
                  transition: "all 0.3s ease",
                }}
              >
                Start Free Trial
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                sx={{
                  borderRadius: 2, textTransform: "none", fontWeight: 600, px: 4, py: 1.6, fontSize: 15,
                  borderColor: "rgba(148,163,184,0.3)", color: "#e0e7ff", bgcolor: "rgba(255,255,255,0.03)", backdropFilter: "blur(10px)",
                  "&:hover": { borderColor: BRAND.cyan, color: BRAND.cyan, bgcolor: "rgba(56,189,248,0.08)" }
                }}
              >
                Back to Top ↑
              </Button>
            </Box>
          </Box>
        </motion.div>

        <Box sx={{ height: "1px", background: `linear-gradient(90deg, transparent 0%, ${BRAND.cyan}66 50%, transparent 100%)`, mb: { xs: 6, md: 8 } }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                <Box component="img" src={logoImg} alt="Talktokam" sx={{ height: 44, width: "auto", borderRadius: 2, boxShadow: `0 8px 24px ${BRAND.cyan}33` }} />
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#fff", letterSpacing: "-0.5px" }}>Talktokam</Typography>
              </Box>
              <Typography sx={{ color: "#94a3b8", fontSize: 13, lineHeight: 1.8, mb: 3, maxWidth: 320 }}>
                AI-powered chatbot management platform that helps businesses build, deploy, and optimize intelligent conversational agents — all from one dashboard.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.2, mb: 3 }}>
                {socialLinks.map((s) => (
                  <motion.a
                    key={s.label}
                    href="#"
                    whileHover={{ y: -3, scale: 1.1 }}
                    style={{ width: 38, height: 38, borderRadius: "10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", backdropFilter: "blur(10px)" }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d={s.d} />
                    </svg>
                  </motion.a>
                ))}
              </Box>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 1.5, py: 0.6, borderRadius: 1, bgcolor: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)" }}>
                <Box sx={{ position: "relative", display: "flex" }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981", boxShadow: "0 0 6px #10b981" }} />
                  <Box sx={{ position: "absolute", width: 6, height: 6, borderRadius: "50%", bgcolor: "#10b981", animation: "talktokamPulse 1.5s ease-out infinite" }} />
                </Box>
                <Typography sx={{ fontSize: 10, color: "#10b981", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px" }}>All Systems Operational</Typography>
              </Box>
            </motion.div>
          </Grid>

          {footerColumns.map((col, i) => (
            <Grid item xs={6} sm={4} md={2} key={col.title}>
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#fff", mb: 2.5, textTransform: "uppercase", letterSpacing: "1.5px" }}>{col.title}</Typography>
                {col.links.map((lnk) => (
                  <Typography key={lnk.l} onClick={() => navigate(lnk.to)} sx={{ display: "block", color: "#94a3b8", fontSize: 13, mb: 1.3, textDecoration: "none", cursor: "pointer", transition: "color 0.2s, transform 0.2s", "&:hover": { color: BRAND.cyan, transform: "translateX(2px)" } }}>
                    {lnk.l}
                  </Typography>
                ))}
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: { xs: 6, md: 8 }, pt: 4, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <Typography sx={{ color: "#64748b", fontSize: 13 }}>
              © 2025 Talktokam, Inc. All rights reserved.
            </Typography>
            <Typography sx={{ color: "#475569", fontSize: 13 }}>·</Typography>
            <Typography sx={{ color: "#94a3b8", fontSize: 13 }}>Made with <span style={{ color: "#ec4899" }}>♥</span> for better conversations</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.4, borderRadius: 1, bgcolor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <Typography sx={{ color: "#94a3b8", fontSize: 11, fontWeight: 600 }}>🌐</Typography>
              <Typography sx={{ color: "#cbd5e1", fontSize: 12, fontWeight: 600 }}>EN</Typography>
            </Box>
            <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 500 }}>v2.0.0</Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
